import React, { createContext, useContext, useState, ReactNode } from "react";
import { MemoryRouter } from "react-router-dom";
import CommonModal from "../components/CommonModal";
import Login from "../components/Login";
import Register from "../components/Register";
import Upload from "../pages/Upload";
import PhotoDetail from "../components/PhotoDetail";

// Define modal types
export type ModalType = "login" | "upload" | "register" | "photo_detail" | null;

// Modal context type
type ModalContextType = {
  openModal: (
    type: ModalType,
    props?: any,
    title?: string,
    size?: "md" | "lg" | "xl"
  ) => void;
  closeModal: () => void;
};

const ModalContext = createContext<ModalContextType | undefined>(undefined);

// Provider component
export const ModalProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [modalType, setModalType] = useState<ModalType>(null);
  const [modalProps, setModalProps] = useState<any>({});
  const [modalTitle, setModalTitle] = useState<string>("");
  const [modalSize, setModalSize] = useState<"md" | "lg" | "xl">("md");

  const openModal = (
    type: ModalType,
    props?: any,
    title?: string,
    size: "md" | "lg" | "xl" = "md"
  ) => {
    setModalType(type);
    setModalProps(props || {});
    setModalTitle(title || type?.toUpperCase() || "");
    setModalSize(size);
  };

  const closeModal = () => {
    setModalType(null);
    setModalProps({});
    setModalTitle("");
    setModalSize("md");
  };

  const renderModalContent = () => {
    switch (modalType) {
      case "login":
        return (
          <MemoryRouter>
            <Login {...modalProps} onClose={closeModal} />
          </MemoryRouter>
        );
      case "register":
        return (
          <MemoryRouter>
            <Register {...modalProps} onClose={closeModal} />
          </MemoryRouter>
        );
      case "upload":
        return (
          <MemoryRouter>
            <Upload {...modalProps} onClose={closeModal} />
          </MemoryRouter>
        );
      case "photo_detail":
        return (
          <MemoryRouter>
            <PhotoDetail {...modalProps} onClose={closeModal} />
          </MemoryRouter>
        );
      default:
        return null;
    }
  };

  const isOpen = modalType !== null;

  return (
    <ModalContext.Provider value={{ openModal, closeModal }}>
      {children}
      <CommonModal
        isOpen={isOpen}
        onClose={closeModal}
        title={modalTitle}
        size={modalSize}
      >
        {renderModalContent()}
      </CommonModal>
    </ModalContext.Provider>
  );
};

// Hook to use the modal context
export const useModal = () => {
  const context = useContext(ModalContext);
  if (!context) {
    throw new Error("useModal must be used within a ModalProvider");
  }
  return context;
};